import { z } from '@repo/schema'

const GetCompanyLocationAddressPointSchema = z
  .union([
    z.object({
      coordinates: z.array(z.number()),
      type: z.literal('Point'),
    }),
    z.string(),
  ])
  .nullish()

const GetCompanyLocationCreatedBySchema = z
  .union([
    z.number(),
    z
      .object({
        id: z.coerce.number(),
      })
      .passthrough(),
  ])
  .nullish()

export const GetCompanyLocationAddressSchema = z.object({
  address: z.string().nullish(),
  address_second_line: z.string().nullish(),
  city: z.string(),
  country: z.string(),
  country_code: z.string().nullish(),
  id: z.number(),
  lat: z.union([z.number(), z.string()]),
  lng: z.union([z.number(), z.string()]),
  place_id: z.string().nullish(),
  place_name: z.string().nullish(),
  point: GetCompanyLocationAddressPointSchema,
  postcode: z.string().nullish(),
  uk_region: z.string().nullish(),
})

export const GetCompanyLocationSchema = z.object({
  address: GetCompanyLocationAddressSchema.nullish(),
  company_id: z.number(),
  created_at: z.coerce.date().nullish(),
  created_by: GetCompanyLocationCreatedBySchema,
  hide_geolocation: z.boolean().nullish(),
  id: z.number(),
  instructions: z.string().nullish(),
  is_active: z.boolean().nullish(),
  name: z.string(),
  organisation_id: z.number().nullish(),
  provider_place_id: z.string().nullish(),
  slug: z.string().nullish(),
  updated_at: z.coerce.date().nullish(),
})

const CoordinateSchema = z
  .union([
    z.number().finite(),
    z
      .string()
      .trim()
      .min(1, 'Coordinate is required')
      .refine(v => {
        const num = Number(v)
        return !Number.isNaN(num) && Number.isFinite(num)
      }, 'Invalid coordinate'),
  ])
  .transform(v => (typeof v === 'string' ? parseFloat(v) : v))

export const PostCompanyLocationSchema = z.object({
  address: z.object({
    address: z.string().trim().max(255),
    address_second_line: z.string().max(255).nullish(),
    city: z.string().trim().min(1, 'City is required').max(255),
    country: z.string().trim().length(2, 'Country is required'),
    lat: CoordinateSchema,
    lng: CoordinateSchema,
    place_id: z.string().nullish(),
    place_name: z.string().nullish(),
    postcode: z.string().max(255).nullish(),
  }),
  hide_geolocation: z.boolean().optional(),
  instructions: z.string().max(160).nullish(),
  is_active: z.boolean().optional(),
  name: z.string().trim().min(1, 'Name is required').max(255),
})

export const PatchCompanyLocationSchema = PostCompanyLocationSchema.partial()

export type CompanyLocation = z.infer<typeof GetCompanyLocationSchema>
export type PatchCompanyLocation = z.infer<typeof PatchCompanyLocationSchema>
export type PostCompanyLocation = z.infer<typeof PostCompanyLocationSchema>
